import React, { useState, useEffect } from 'react';

/**
 * Renders its children AFTER the browser is idle or the user interacts.
 *
 * Purpose: keep heavy non-critical components (e.g. the ChatWidget) out of
 * the initial render path so they don't contribute to Total Blocking Time.
 * The user still gets the component within a few hundred milliseconds of
 * FCP — well before they'd try to click it.
 *
 * Fires on the EARLIEST of:
 *   1. requestIdleCallback (or a 1.5s setTimeout fallback on Safari)
 *   2. First pointerdown/keydown/scroll/touchstart (guarantees visibility
 *      the moment the user shows any intent)
 */
export default function LazyMountAfterIdle({ children, timeout = 2000 }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return undefined;

    let cancelled = false;
    const show = () => {
      if (!cancelled) setReady(true);
    };

    let idleHandle = null;
    let timeoutHandle = null;
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleHandle = window.requestIdleCallback(show, { timeout });
    } else {
      timeoutHandle = setTimeout(show, 1500);
    }

    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((ev) => {
      window.addEventListener(ev, show, { once: true, passive: true, capture: true });
    });

    return () => {
      cancelled = true;
      if (idleHandle && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle) clearTimeout(timeoutHandle);
      events.forEach((ev) => {
        window.removeEventListener(ev, show, { capture: true });
      });
    };
  }, [ready, timeout]);

  return ready ? children : null;
}
